import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listarClientesTodos,
  ativarClienteZenPass,
  editarClienteZenPass,
  obterClienteZenPassPorId,
} from "../services/api";
import { Search, Save } from "lucide-react";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const FormularioZenPass = ({ token }) => {
  const { showError, showSuccess, showWarning, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id !== undefined;

  const [clientes, setClientes] = useState([]);
  const [clientesOriginais, setClientesOriginais] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [slug, setSlug] = useState("");
  const [maxFila, setMaxFila] = useState(80);
  const [descricao, setDescricao] = useState("");
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const resp = await listarClientesTodos(token, { pageSize: 2000, sortBy: "razaosocial" });
        const data = resp?.data ?? resp;
        const arr = Array.isArray(data) ? data : data?.items || data?.Items || [];
        setClientes(arr);
        setClientesOriginais(arr);
      } catch (err) {
        setError("Erro ao carregar clientes.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchClientes();
  }, [token]);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchLicenca = async () => {
      try {
        const response = await obterClienteZenPassPorId(id, token);
        const lic = response?.data;
        setSelectedClientId(String(lic?.clienteId ?? ""));
        setSlug(lic?.slug ?? "");
        setMaxFila(Number(lic?.maxFila ?? 80));
        setDescricao(lic?.descricao ?? "");
      } catch (err) {
        setError("Erro ao carregar a licença ZenPass.");
        console.error(err);
      }
    };
    fetchLicenca();
  }, [id, isEditMode, token]);

  const clienteSelecionado = useMemo(() => {
    const idNum = Number(selectedClientId);
    return (clientesOriginais || []).find((c) => c.id === idNum) || null;
  }, [selectedClientId, clientesOriginais]);

  const handleBuscar = (e) => {
    const termo = e.target.value;
    setBusca(termo);
    const t = termo.toLowerCase();
    setClientes(
      (clientesOriginais || []).filter(
        (c) =>
          (c.cnpj || "").toLowerCase().includes(t) ||
          (c.razaoSocial || "").toLowerCase().includes(t) ||
          (c.nomeFantasia || "").toLowerCase().includes(t)
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditMode && !selectedClientId) {
      showWarning("Selecione um cliente.");
      return;
    }
    if (!isEditMode && !slug.trim()) {
      showWarning("Informe o slug do restaurante.");
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await editarClienteZenPass(id, { descricao, maxFila: Number(maxFila) }, token);
        showSuccess("ZenPass atualizado.", () => navigate("/licenciamento/zenpass/list"));
      } else {
        await ativarClienteZenPass(
          {
            clienteId: Number(selectedClientId),
            revendaId: clienteSelecionado?.revendaId ?? 0,
            slug: slug.trim(),
            descricao,
            maxFila: Number(maxFila) || 80,
          },
          token
        );
        showSuccess("ZenPass ativado.", () => navigate("/licenciamento/zenpass/list"));
      }
    } catch (err) {
      showError(err, "Erro ao salvar ZenPass.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-center">Carregando clientes...</div>;
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <>
      <FeedbackAlert />
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
        <div className="zen-card rounded-2xl p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {isEditMode ? "Editar ZenPass" : "Nova licença ZenPass"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300 mb-6">
            O slug é o identificador do restaurante no ZenPass. O limite da fila vale na operação.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar cliente"
                value={busca}
                onChange={handleBuscar}
                disabled={isEditMode}
                className="pl-10 pr-4 py-2.5 w-full rounded-xl border zen-card dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
              />
            </div>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              disabled={isEditMode}
              className="w-full p-2.5 rounded-xl border zen-card dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
            >
              <option value="">Selecione o cliente</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nomeFantasia || c.razaoSocial} {c.cnpj ? `· ${c.cnpj}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={isEditMode}
              placeholder="slug do restaurante"
              className="w-full p-2.5 rounded-xl border zen-card dark:bg-slate-950/40 dark:border-white/10 dark:text-white disabled:opacity-60"
            />
            <input
              type="number"
              min={1}
              value={maxFila}
              onChange={(e) => setMaxFila(e.target.value)}
              placeholder="Limite da fila"
              className="w-full p-2.5 rounded-xl border zen-card dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
            />
          </div>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descrição"
            className="w-full p-2.5 rounded-xl border zen-card mb-4 dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
          />
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </>
  );
};

export default FormularioZenPass;
